import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Landmark, Lock, Truck, Zap } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { api, money } from "../lib/api";

const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

const field =
  "w-full rounded-lg border border-white/10 bg-[#141417] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-200 focus:border-[#E8E9EB]/60";
const label = "mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500";

const Section = ({ num, title, children, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="glass rounded-2xl p-6 sm:p-7"
    data-testid={testid}
  >
    <div className="mb-5 flex items-center gap-3">
      <span className="font-mono text-xs text-[#6E7683]">/{num}</span>
      <h2 className="font-display text-lg font-semibold tracking-tight text-white">{title}</h2>
    </div>
    {children}
  </motion.section>
);

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [eligible, setEligible] = useState(false);
  const [offerDismissed, setOfferDismissed] = useState(() => sessionStorage.getItem("ap_offer_dismissed") === "1");
  const [hasToken] = useState(() => !!localStorage.getItem("ap_customer_token"));
  const [form, setForm] = useState({
    email: "", phone: "", firstName: "", lastName: "", street: "", apartment: "",
    suburb: "", state: "NSW", postcode: "", country: "Australia", shipping: "standard", discount: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const t = localStorage.getItem("ap_customer_token");
    if (!t) return;
    const headers = { headers: { Authorization: `Bearer ${t}` } };
    api.get("/auth/me", headers)
      .then((r) => {
        setAccountEmail(r.data.email);
        setForm((f) => ({
          ...f,
          email: r.data.email,
          firstName: f.firstName || (r.data.name || "").split(" ")[0] || "",
          lastName: f.lastName || (r.data.name || "").split(" ").slice(1).join(" "),
        }));
      })
      .catch(() => {});
    api.get("/account/discount", headers)
      .then((r) => {
        if (r.data.eligible) {
          setEligible(true);
          setForm((f) => ({ ...f, discount: r.data.code }));
        }
      })
      .catch(() => {});
  }, []);

  const emailMatchesAccount = accountEmail && form.email.trim().toLowerCase() === accountEmail.toLowerCase();
  const discount = eligible && emailMatchesAccount && form.discount.trim().toUpperCase() === "ALPHA10" ? subtotal * 0.1 : 0;
  const shippingCost = form.shipping === "express" ? 24.95 : 9.95;
  const total = Math.max(0, subtotal - discount + shippingCost);

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const t = localStorage.getItem("ap_customer_token");
      const res = await api.post("/orders/bank", {
        email: form.email,
        phone: form.phone,
        first_name: form.firstName,
        last_name: form.lastName,
        street: form.street,
        apartment: form.apartment,
        suburb: form.suburb,
        state: form.state,
        postcode: form.postcode,
        country: form.country,
        shipping_method: form.shipping,
        discount_code: form.discount,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
      }, t ? { headers: { Authorization: `Bearer ${t}` } } : undefined);
      clearCart();
      toast.success("Order placed. Payment instructions sent to your email.");
      navigate(`/order/${res.data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order. Try again.");
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-5" data-testid="checkout-empty">
        <p className="text-slate-400">Your cart is empty.</p>
        <Link to="/products" data-testid="checkout-empty-shop-link" className="font-mono text-xs tracking-[0.2em] text-[#E8E9EB]">BROWSE SHOP PRODUCTS</Link>
      </div>
    );
  }

  const showOffer = !hasToken && !offerDismissed;

  return (
    <div className="spotlight mx-auto max-w-6xl px-5 pb-24 pt-28 sm:px-8" data-testid="checkout-page">
      {showOffer && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-5 backdrop-blur-sm" data-testid="checkout-signup-popup">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="glass w-full max-w-md rounded-2xl p-8 text-center"
          >
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">MEMBER OFFER</div>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-white">
              Get 10% off your first order
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Create a free Alpha Peps account and code ALPHA10 applies automatically to your first order.
              One use per customer.
            </p>
            <button
              type="button"
              onClick={() => navigate("/account?next=/checkout")}
              data-testid="popup-signup-button"
              className="mt-6 w-full rounded-full bg-[#E8E9EB] py-3.5 font-mono text-xs tracking-[0.25em] text-[#050811] transition-colors duration-200 hover:bg-white"
            >
              SIGN UP & SAVE 10%
            </button>
            <button
              type="button"
              onClick={() => { sessionStorage.setItem("ap_offer_dismissed", "1"); setOfferDismissed(true); }}
              data-testid="popup-guest-button"
              className="mt-3 w-full rounded-full border border-white/10 py-3.5 font-mono text-xs tracking-[0.25em] text-slate-400 transition-colors duration-200 hover:border-white/25 hover:text-white"
            >
              CONTINUE WITHOUT DISCOUNT
            </button>
          </motion.div>
        </div>
      )}
      <Link to="/products" data-testid="checkout-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-slate-500 transition-colors duration-200 hover:text-[#E8E9EB]">
        <ArrowLeft className="h-3.5 w-3.5" /> CONTINUE SHOPPING
      </Link>
      <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="checkout-heading">
        Checkout
      </h1>

      <form onSubmit={submit} className="mt-10 grid items-start gap-8 lg:grid-cols-[1.25fr_1fr]">
        <div className="flex flex-col gap-6">
          <Section num="01" title="Contact information" testid="checkout-contact-section">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <span className={label}>EMAIL</span>
                <input required type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
              </div>
              <div>
                <span className={label}>PHONE</span>
                <input required type="tel" placeholder="04XX XXX XXX" value={form.phone} onChange={set("phone")} className={field} data-testid="contact-phone-input" />
              </div>
            </div>
          </Section>

          <Section num="02" title="Shipping address" testid="checkout-address-section">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <span className={label}>FIRST NAME</span>
                <input required placeholder="First name" value={form.firstName} onChange={set("firstName")} className={field} data-testid="ship-first-name-input" />
              </div>
              <div>
                <span className={label}>LAST NAME</span>
                <input required placeholder="Last name" value={form.lastName} onChange={set("lastName")} className={field} data-testid="ship-last-name-input" />
              </div>
            </div>
            <div className="mt-4">
              <span className={label}>STREET ADDRESS</span>
              <input required placeholder="Street address" value={form.street} onChange={set("street")} className={field} data-testid="ship-street-input" />
            </div>
            <div className="mt-4">
              <span className={label}>APARTMENT, SUITE, ETC. (OPTIONAL)</span>
              <input placeholder="Apartment, suite, unit (optional)" value={form.apartment} onChange={set("apartment")} className={field} data-testid="ship-apartment-input" />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <span className={label}>SUBURB</span>
                <input required placeholder="Suburb" value={form.suburb} onChange={set("suburb")} className={field} data-testid="ship-suburb-input" />
              </div>
              <div>
                <span className={label}>STATE</span>
                <select value={form.state} onChange={set("state")} className={field} data-testid="ship-state-select">
                  {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <span className={label}>POSTCODE</span>
                <input required placeholder="2000" value={form.postcode} onChange={set("postcode")} className={field} data-testid="ship-postcode-input" />
              </div>
            </div>
            <div className="mt-4">
              <span className={label}>COUNTRY</span>
              <select value={form.country} onChange={set("country")} className={field} data-testid="ship-country-select">
                <option value="Australia">Australia</option>
              </select>
            </div>
          </Section>

          <Section num="03" title="Shipping method" testid="checkout-shipping-section">
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { id: "standard", icon: Truck, name: "Standard Cold-Chain", desc: "3-5 business days, tracked", price: "A$9.95" },
                { id: "express", icon: Zap, name: "Express Cold-Chain", desc: "1-2 business days, priority dispatch", price: "A$24.95" },
              ].map((m) => (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => setForm({ ...form, shipping: m.id })}
                  data-testid={`shipping-method-${m.id}`}
                  className={`rounded-xl border p-4 text-left transition-colors duration-200 ${
                    form.shipping === m.id ? "border-[#E8E9EB]/60 bg-[#E8E9EB]/5" : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <m.icon className="h-5 w-5 text-[#E8E9EB]" />
                    <span className="font-mono text-xs text-white">{m.price}</span>
                  </div>
                  <div className="mt-3 text-sm font-medium text-white">{m.name}</div>
                  <div className="mt-1 text-xs text-slate-500">{m.desc}</div>
                </button>
              ))}
            </div>
          </Section>

          <Section num="04" title="Payment method" testid="checkout-payment-section">
            <div className="flex flex-col gap-4">
              <div
                data-testid="payment-bank-transfer-option"
                className="rounded-xl border border-[#E8E9EB]/60 bg-[#E8E9EB]/5 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Landmark className="h-5 w-5 text-[#E8E9EB]" />
                    <span className="text-sm font-medium text-white">Bank Transfer (Osko / PayID)</span>
                  </div>
                  <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-emerald-400">SELECTED</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">
                  Pay instantly via Osko/PayID or standard bank transfer. Full payment instructions are emailed to you
                  immediately after you place your order. Orders dispatch within 24 hours of payment clearing.
                </p>
              </div>
            </div>
          </Section>
        </div>

        <motion.aside
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass sticky top-24 rounded-2xl p-6 sm:p-7"
          data-testid="checkout-order-summary"
        >
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">ORDER SUMMARY</div>
          <div className="mt-4 flex flex-col divide-y divide-white/5">
            {items.map((i) => (
              <div key={i.id} className="flex items-center justify-between py-3 text-sm" data-testid={`summary-item-${i.id}`}>
                <span className="text-slate-300">{i.name} <span className="font-mono text-xs text-slate-500">{i.dose} x{i.qty}</span></span>
                <span className="font-mono text-white">{money(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <span className={label}>DISCOUNT CODE</span>
            <input placeholder="Enter code" value={form.discount} onChange={set("discount")} className={field} data-testid="checkout-discount-input" />
            {eligible && emailMatchesAccount ? (
              <p className="mt-2 text-[11px] text-emerald-400" data-testid="discount-hint-applied">ALPHA10 applied — 10% off your first order</p>
            ) : accountEmail && !eligible ? (
              <p className="mt-2 text-[11px] text-slate-500" data-testid="discount-hint-used">First-order discount already used on your account.</p>
            ) : (
              <p className="mt-2 text-[11px] text-slate-500" data-testid="discount-hint-signup">
                Members only — <Link to="/account?next=/checkout" className="text-[#E8E9EB] underline underline-offset-2">sign up</Link> to get 10% off your first order.
              </p>
            )}
          </div>
          <div className="mt-4 space-y-1.5 border-t border-white/10 pt-4 font-mono text-xs">
            <div className="flex justify-between text-slate-400"><span>SUBTOTAL</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between text-emerald-400"><span>DISCOUNT</span><span data-testid="checkout-discount-amount">-{money(discount)}</span></div>
            <div className="flex justify-between text-slate-400"><span>SHIPPING</span><span data-testid="checkout-shipping-cost">{money(shippingCost)}</span></div>
            <div className="flex justify-between pt-2 text-sm text-white"><span>TOTAL</span><span className="text-[#E8E9EB]" data-testid="checkout-total">{money(total)}</span></div>
          </div>
          <button
            type="submit"
            disabled={placing}
            data-testid="checkout-submit-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#6E7683] py-3.5 font-mono text-xs tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811] disabled:opacity-50"
          >
            <Lock className="h-3.5 w-3.5" />
            {placing ? "PLACING ORDER..." : `PLACE ORDER - ${money(total)}`}
          </button>
          <p className="mt-3 text-center font-mono text-[10px] tracking-[0.1em] text-slate-600">
            SECURE BANK TRANSFER - OSKO / PAYID
          </p>
          <p className="mt-2 text-center font-mono text-[10px] tracking-[0.1em] text-slate-600" data-testid="refund-policy-note">
            ALL SALES ARE FINAL ONCE DISPATCHED - ORDERS CANNOT BE REFUNDED AFTER DISPATCH
          </p>
        </motion.aside>
      </form>
    </div>
  );
}
